import { map } from "lodash";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable, HtmlContent } from "../../../../common/components";
import { DataTableHeader } from "../../../../common/components/DataTable/DataTableHeader";
import { PermissionsGate } from "../../../../common/components/PermissionsGate";
import { PERMISSIONS } from "../../../../common/constants";
import {
    useDynamicRefs,
    usePaginatorProps,
    useSortableProps,
} from "../../../../common/hooks";
import {
    ColumnOptions,
    DataTableSettings,
    FilterConfiguration,
    TableTemplate,
    UpdateDataTableSettingsFunction,
} from "../../../../common/models/table.model";
import { formatDateTime } from "../../../../common/utils/date.utils";
import {
    ANNOUNCEMENT_MODALS,
    ANNOUNCEMENT_TABLE_SORT_TYPES,
} from "../../constants";
import { StatusFilterTemplate } from "./templates/filter";
import { Announcement } from "../../models";
import { getAnnouncementTranslation } from "../../utils";
import { AnnouncementModal } from "../AnnouncementModal";
import { DeleteAnnouncementModal } from "../DeleteAnnouncementModal";
import { ActionsBodyTemplate, StatusTagBodyTemplate } from "./columnBody";

const TEMPLATES: TableTemplate[] = [
    {
        type: "STATUS",
        Component: StatusFilterTemplate,
    },
];

interface AnnouncementsTableProps {
    id: string;
    announcements: Announcement[];
    settings: DataTableSettings;
    tableProps: {
        itemCount: number;
        pageCount: number;
    };
    getAnnouncementsRequestData: UpdateDataTableSettingsFunction;
    isLoadingAnnouncements?: boolean;
    locale?: string;
    filterConfigurations?: FilterConfiguration[];
    isLoadingFilterConfig?: boolean;
    onCreateSuccess?: () => void;
    onEditSuccess?: () => void;
    onDeleteSuccess?: () => void;
}

interface FormattedAnnouncement extends Announcement {
    formattedStartDateTime: string;
    formattedEndDateTime: string;
    formattedTitle: string;
    formattedDescription: string;
}

const AnnouncementsTable = (props: AnnouncementsTableProps) => {
    const {
        id,
        announcements = [],
        settings,
        tableProps,
        getAnnouncementsRequestData,
        isLoadingAnnouncements = false,
        locale,
        filterConfigurations = [],
        isLoadingFilterConfig = false,
        onCreateSuccess,
        onEditSuccess,
        onDeleteSuccess,
    } = props;

    const { t } = useTranslation(["common", "announcements"]);

    const [selectedAnnouncement, setSelectedAnnouncement] =
        useState<Announcement | null>(null);
    const [activeModal, setActiveModal] = useState<string | null>(null);

    const { getRef: getMenuRef } = useDynamicRefs<Menu>();

    const handleCloseModal = () => setActiveModal(null);
    const handleClearSelection = () => setSelectedAnnouncement(null);
    const handleAddClick = () => setActiveModal(ANNOUNCEMENT_MODALS.CREATE);
    const handleEditClick = (announcement: Announcement) => {
        setSelectedAnnouncement(announcement);
        setActiveModal(ANNOUNCEMENT_MODALS.EDIT);
    };
    const handleDeleteClick = (announcement: Announcement) => {
        setSelectedAnnouncement(announcement);
        setActiveModal(ANNOUNCEMENT_MODALS.DELETE);
    };

    const formattedAnnouncements: FormattedAnnouncement[] = useMemo(
        () =>
            map(announcements, (announcement: Announcement) => {
                const translation = getAnnouncementTranslation(
                    announcement.translations,
                    locale
                );

                return {
                    ...announcement,
                    formattedStartDateTime: formatDateTime(
                        announcement.startDateTime
                    ),
                    formattedEndDateTime: formatDateTime(
                        announcement.endDateTime
                    ),
                    formattedTitle: translation?.title ?? "",
                    formattedDescription: translation?.description ?? "",
                };
            }),
        [announcements, locale]
    );

    const columns = useMemo<ColumnOptions[]>(
        () => [
            {
                columnName: "startDateTime",
                props: {
                    field: "formattedStartDateTime",
                    sortable: true,
                    isDefaultSortField: true,
                    sortField: ANNOUNCEMENT_TABLE_SORT_TYPES.START_DATE_TIME,
                    header: t("announcements:table.start"),
                    headerClassName: "w-px whitespace-nowrap",
                    bodyClassName: "w-px whitespace-nowrap",
                },
            },
            {
                columnName: "endDateTime",
                props: {
                    field: "formattedEndDateTime",
                    sortable: true,
                    sortField: ANNOUNCEMENT_TABLE_SORT_TYPES.END_DATE_TIME,
                    header: t("announcements:table.end"),
                    headerClassName: "w-px whitespace-nowrap",
                    bodyClassName: "w-px whitespace-nowrap",
                },
            },
            {
                columnName: "formattedTitle",
                props: {
                    field: "formattedTitle",
                    sortable: true,
                    sortField: ANNOUNCEMENT_TABLE_SORT_TYPES.TITLE,
                    header: t("announcements:table.title"),
                },
            },
            {
                columnName: "formattedDescription",
                props: {
                    field: "formattedDescription",
                    className: "max-w-xs",
                    header: t("common:description"),
                    body: (rowData: FormattedAnnouncement) => (
                        <HtmlContent html={rowData.formattedDescription} className="line-clamp-2" />
                    ),
                },
            },
            {
                columnName: "createdBy.username",
                props: {
                    field: "createdBy.username",
                    sortable: true,
                    sortField: ANNOUNCEMENT_TABLE_SORT_TYPES.CREATED_BY,
                    body: (rowData) => rowData.createdBy?.username ?? "-",
                    header: t("announcements:table.createdBy"),
                },
            },
            {
                columnName: "status",
                props: {
                    field: "status",
                    header: t("announcements:table.status"),
                    headerClassName: "w-px whitespace-nowrap",
                    bodyClassName: "w-px whitespace-nowrap",
                    body: (rowData) => (
                        <StatusTagBodyTemplate rowData={rowData} />
                    ),
                },
            },
            {
                columnName: "actions",
                props: {
                    body: (rowData) => (
                        <ActionsBodyTemplate
                            rowData={rowData}
                            getMenuRef={getMenuRef}
                            onEdit={handleEditClick}
                            onDelete={handleDeleteClick}
                        />
                    ),
                },
            },
        ],
        [t, getMenuRef]
    );

    const paginatorProps = usePaginatorProps({
        tablePageSettings: settings,
        onPageSettingsChanged: getAnnouncementsRequestData,
        itemCount: tableProps?.itemCount,
    });

    const { sortTableProps } = useSortableProps({
        dataTableSettings: settings,
        onDataTableSettingsChanged: getAnnouncementsRequestData,
        isLoading: isLoadingAnnouncements,
        columns,
    });

    const dataTableHeader = useMemo(
        () => (
            <div className="flex items-center gap-4 w-full min-w-0">
                <div className="flex-1 min-w-0">
                    <DataTableHeader
                        tableId={id}
                        showFilter
                        showDateRangePicker
                        clearableDateRange
                        allowFutureDates
                        dataTableSettings={settings}
                        onDataTableSettingsChanged={getAnnouncementsRequestData}
                        columns={columns}
                        filterConfigurations={filterConfigurations}
                        isLoadingFilterConfig={isLoadingFilterConfig}
                        templates={TEMPLATES}
                    />
                </div>
                <PermissionsGate
                    globalPermissions={[PERMISSIONS.ANNOUNCEMENT.CREATE]}
                >
                    <Button
                        icon="pi pi-plus"
                        iconPos="right"
                        onClick={handleAddClick}
                        severity="secondary"
                        className="h-12 shrink-0"
                        label={t("common:add")}
                    />
                </PermissionsGate>
            </div>
        ),
        [
            id,
            settings,
            getAnnouncementsRequestData,
            columns,
            filterConfigurations,
            isLoadingFilterConfig,
            t,
        ]
    );

    return (
        <>
            <DataTable
                id={id}
                className="[&_.p-datatable-header]:pt-0"
                loading={isLoadingAnnouncements}
                value={formattedAnnouncements}
                header={dataTableHeader}
                {...paginatorProps}
                {...sortTableProps}
                emptyMessage={t("common:noResultsFound") ?? ""}
                pt={{
                    wrapper: {
                        className: "pb-8",
                    },
                }}
                stripedRows
                scrollable
                scrollHeight="flex"
            >
                {columns.map((colProps: ColumnOptions, index: number) => (
                    <Column
                        key={`announcements-table-column-${index}`}
                        {...colProps.props}
                    />
                ))}
            </DataTable>
            <AnnouncementModal
                isOpen={
                    activeModal === ANNOUNCEMENT_MODALS.CREATE ||
                    activeModal === ANNOUNCEMENT_MODALS.EDIT
                }
                onClickClose={handleCloseModal}
                onCloseAnimationEnd={handleClearSelection}
                onSuccess={
                    activeModal === ANNOUNCEMENT_MODALS.EDIT
                        ? onEditSuccess
                        : onCreateSuccess
                }
                announcement={selectedAnnouncement}
            />
            <DeleteAnnouncementModal
                isOpen={activeModal === ANNOUNCEMENT_MODALS.DELETE}
                onClose={handleCloseModal}
                onCloseAnimationEnd={handleClearSelection}
                announcement={selectedAnnouncement}
                onSuccess={onDeleteSuccess}
            />
        </>
    );
};

export default AnnouncementsTable;
